export const HEADER_MAIN_GAP = 24

/**
 * Определяет направление будущего скролла относительно целевого элемента.
 * @param {Element} target - Элемент, к которому планируется прокрутка.
 * @returns {'down' | 'up'} Направление прокрутки: вниз или вверх.
 */
function getScrollDirection(target) {
  return target.getBoundingClientRect().top > 0 ? 'down' : 'up'
}

/**
 * Возвращает ближайший Element для цели события.
 * @param {EventTarget | null} target - Цель события.
 * @returns {Element | null} DOM-элемент или родитель текстового узла.
 */
export function getElementFromEventTarget(target) {
  if (target instanceof Element) return target
  if (target instanceof Node) return target.parentElement

  return null
}

/**
 * Проверяет, является ли ссылка якорной ссылкой текущего сайта.
 * @param {Element | null | undefined} anchor - Элемент ссылки.
 * @returns {boolean} `true`, если `href` начинается с `#` или `/#`.
 */
export function isHashLink(anchor) {
  if (!(anchor instanceof HTMLAnchorElement)) return false

  const href = anchor.getAttribute('href') || ''
  return href.startsWith('#') || href.startsWith('/#')
}

/**
 * Проверяет, ведёт ли якорная ссылка на текущую страницу.
 * @param {Element | null | undefined} anchor - Элемент ссылки.
 * @returns {boolean} `true`, если ссылка указывает на текущую страницу и содержит hash.
 */
export function isSamePageHashLink(anchor) {
  if (!isHashLink(anchor)) return false

  try {
    const url = new URL(anchor.href, window.location.origin)
    return (
      url.origin === window.location.origin &&
      url.pathname === window.location.pathname &&
      !!url.hash
    )
  } catch {
    return false
  }
}

/**
 * Находит целевой DOM-элемент по строке `href` или `hash`.
 * @param {string} href - Значение `href`, абсолютный URL или hash.
 * @returns {HTMLElement | null} Найденный элемент или `null`, если цель не определена.
 */
export function resolveTargetFromHref(href) {
  if (typeof href !== 'string' || !href) return null

  let hash = href

  if (hash.startsWith('/#')) {
    hash = hash.slice(1)
  } else if (hash.startsWith('http://') || hash.startsWith('https://')) {
    try {
      hash = new URL(hash, window.location.origin).hash
    } catch {
      return null
    }
  }

  if (!hash.startsWith('#') || hash === '#') return null

  try {
    return document.querySelector(hash)
  } catch {
    const id = hash.slice(1)
    return document.getElementById(id)
  }
}

/**
 * Возвращает элементы шапки, влияющие на расчёт вертикального отступа при скролле.
 * @returns {{ header: HTMLElement | null, headerTop: Element | null }} Объект с основной шапкой и её верхней частью.
 */
export function getHeaderElements() {
  const header = document.querySelector(
    'header[data-controller~="header"], header'
  )
  const headerTop = header?.querySelector('.header__top') || null

  return { header, headerTop }
}

/**
 * Возвращает высоту панели якорей страницы, если она уже липкая или станет
 * липкой при скролле вниз.
 * @param {Object} [options={}] - Параметры расчёта.
 * @param {'down' | 'up' | null} [options.scrollDirection=null] - Направление предстоящего скролла.
 * @returns {number} Высота панели в пикселях.
 */
export function getStickyPageAnchorsHeight(options = {}) {
  const { scrollDirection = null } = options
  const pageAnchors = document.querySelector('.page-anchors')
  const anchorsContent = pageAnchors?.querySelector('.page-anchors__content')
  const shouldAccountAnchors =
    pageAnchors?.classList.contains('is-sticky') || scrollDirection === 'down'

  if (!anchorsContent || !shouldAccountAnchors) return 0

  return anchorsContent.offsetHeight
}

/**
 * Возвращает высоту видимой мобильной панели оглавления.
 * @returns {number} Высота панели в пикселях.
 */
export function getVisibleTocPanelHeight() {
  const mobilePanel = document.querySelector(
    '[data-toc-block-target="mobilePanel"].is-visible'
  )

  if (!mobilePanel) return 0

  return mobilePanel.offsetHeight
}

/**
 * Возвращает высоту закреплённой строки поиска каталога (клубы, тренеры, продукты).
 * @returns {number} Высота панели в пикселях.
 */
export function getVisibleStickySearchHeight() {
  const content = document.querySelector(
    '.sticky-search.is-sticky .sticky-search__content'
  )

  if (!content) return 0

  return content.offsetHeight
}

/**
 * Вычисляет суммарный отступ, который нужно учесть при прокрутке к якорю.
 * @param {Object} [options={}] - Параметры расчёта.
 * @param {boolean} [options.includeHeader=true] - Учитывать ли шапку сайта.
 * @param {boolean} [options.includePageAnchors=true] - Учитывать ли панель якорей страницы.
 * @param {boolean} [options.includeTocPanel=true] - Учитывать ли мобильную панель оглавления.
 * @param {boolean} [options.includeStickySearch=true] - Учитывать ли закреплённую строку поиска каталога.
 * @param {boolean} [options.includeGap=true] - Добавлять ли базовый зазор `HEADER_MAIN_GAP`.
 * @param {'down' | 'up'} [options.scrollDirection='down'] - Направление предстоящего скролла.
 * @returns {number} Итоговый вертикальный отступ в пикселях.
 */
export function getAnchorOffset(options = {}) {
  const {
    includeHeader = true,
    includePageAnchors = true,
    includeTocPanel = true,
    includeStickySearch = true,
    includeGap = true,
    scrollDirection = 'down'
  } = options

  let offset = 0
  const pageAnchorsHeight = includePageAnchors
    ? getStickyPageAnchorsHeight({ scrollDirection })
    : 0

  if (includeHeader) {
    const { header, headerTop } = getHeaderElements()
    const headerHeight = header?.offsetHeight || 0
    const headerTopHeight = headerTop?.offsetHeight || 0
    const isHeaderHiddenTop = header?.classList.contains('header--hidden-top')

    let visibleHeaderHeight = headerHeight

    if (header) {
      if (scrollDirection === 'down' && pageAnchorsHeight > 0) {
        visibleHeaderHeight = 0
      } else if (isHeaderHiddenTop || scrollDirection === 'down') {
        visibleHeaderHeight -= headerTopHeight
      }
    }

    offset += Math.max(0, visibleHeaderHeight)
  }

  if (includePageAnchors) {
    offset += pageAnchorsHeight
  }

  if (includeTocPanel) {
    offset += getVisibleTocPanelHeight()
  }

  if (includeStickySearch) {
    offset += getVisibleStickySearchHeight()
  }

  if (includeGap) {
    offset += HEADER_MAIN_GAP
  }

  return offset
}

/**
 * Прокручивает окно к указанному элементу с учётом шапки и липких панелей.
 * @param {HTMLElement} target - Целевой элемент, к которому нужно прокрутить страницу.
 * @param {Object} [options={}] - Параметры прокрутки.
 * @param {ScrollBehavior} [options.behavior='smooth'] - Режим прокрутки.
 * @param {boolean} [options.updateHash=false] - Обновлять ли hash в адресной строке после прокрутки.
 * @param {boolean} [options.includeHeader=true] - Учитывать ли высоту шапки.
 * @param {boolean} [options.includePageAnchors=true] - Учитывать ли панель якорей страницы.
 * @param {boolean} [options.includeTocPanel=true] - Учитывать ли мобильную панель оглавления.
 * @param {boolean} [options.includeGap=true] - Добавлять ли базовый зазор после липких элементов.
 * @returns {void}
 */
export function scrollToElement(target, options = {}) {
  if (!(target instanceof HTMLElement)) return

  const {
    behavior = 'smooth',
    updateHash = false,
    includeHeader = true,
    includePageAnchors = true,
    includeTocPanel = true,
    includeGap = true
  } = options

  const offset = getAnchorOffset({
    includeHeader,
    includePageAnchors,
    includeTocPanel,
    includeGap,
    scrollDirection: getScrollDirection(target)
  })

  const targetTop = target.getBoundingClientRect().top + window.scrollY - offset

  window.scrollTo({
    top: Math.max(0, targetTop),
    behavior
  })

  if (updateHash && target.id) {
    window.history.pushState(null, '', `#${target.id}`)
  }
}

/**
 * Находит элемент по hash и прокручивает страницу к нему.
 * @param {string} hash - Hash, `href` или URL, из которого можно извлечь цель прокрутки.
 * @param {Object} [options={}] - Параметры, передаваемые в `scrollToElement`.
 * @returns {boolean} `true`, если целевой элемент найден и прокрутка запущена.
 */
export function scrollToHash(hash, options = {}) {
  const target = resolveTargetFromHref(hash)
  if (!target) return false

  scrollToElement(target, options)
  return true
}
